import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

const SCROLL_ROOT_SELECTOR = '.main-content'
const DEFAULT_TARGET_ID = 'cook-browse-toolbar'

/**
 * Floating back-to-top control for the Cook Browse screen.
 * Appears once the filter toolbar scrolls out of view.
 */
export default function CookBrowseBackToTop({
  targetId = DEFAULT_TARGET_ID,
  enabled = true,
}) {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (!enabled) {
      setVisible(false)
      return undefined
    }

    const scrollRoot = document.querySelector(SCROLL_ROOT_SELECTOR)
    const target = document.getElementById(targetId)
    if (!scrollRoot || !target) return undefined

    const observer = new IntersectionObserver(
      ([entry]) => setVisible(!entry.isIntersecting),
      { root: scrollRoot, threshold: 0 },
    )
    observer.observe(target)
    return () => observer.disconnect()
  }, [enabled, targetId])

  const scrollToTop = useCallback(() => {
    const scrollRoot = document.querySelector(SCROLL_ROOT_SELECTOR)
    if (!scrollRoot) return

    const mediaQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)')
    const reduceMotion = Boolean(mediaQuery?.matches)
    const behavior = reduceMotion ? 'auto' : 'smooth'

    scrollRoot.scrollTo({ top: 0, behavior })
  }, [])

  if (!enabled || !visible) return null

  return createPortal(
    <button
      type="button"
      className="cook-browse-back-to-top"
      onClick={scrollToTop}
      aria-label="Back to top"
      data-testid="cook-browse-back-to-top"
    >
      <i className="fas fa-arrow-up" aria-hidden="true" />
    </button>,
    document.body,
  )
}
